import { ethereum } from '@0xtorch/core'
import { type Client, createClient } from '../../client'
import { type Explorer, createEtherscanV2 } from '../../explorer'
import type { LowerHex } from '../../types'
import type { Chain } from '../types/chain'
import type { HttpRpc } from '../types/rpc'
import { robinhood } from '../viemChains/robinhood'

/**
 * Robinhood Chain mainnet を作成する。
 * @param params.explorerApiKey - Etherscan V2 API キー。
 * @param params.explorerPageSize - 履歴 API のページサイズ。
 * @param params.explorerProxyUrl - Explorer API のプロキシ URL。
 * @returns RPC と Etherscan V2 を接続した chain 定義。
 */
export const createRobinhoodChain = ({
  explorerApiKey: apiKey,
  explorerProxyUrl: proxyUrl,
  explorerPageSize: pageSize,
}: {
  explorerApiKey?: string
  explorerProxyUrl?: string
  explorerPageSize?: number
}): Chain =>
  createRobinhoodChainCustom({
    client: createClient({ chain: robinhood, httpRpcs: robinhoodHttpRpcs }),
    explorer: createEtherscanV2({
      name: 'Robinhood Etherscan',
      baseUrl: 'https://robin.etherscan.io',
      chainId: 4663,
      apiKey,
      pageSize,
      proxyUrl,
    }),
  })

/**
 * Robinhood Chain の依存を指定して chain 定義を作成する。
 * @param params.client - RPC client。
 * @param params.explorer - Explorer 実装。
 * @returns Robinhood Chain の chain 定義。
 */
export const createRobinhoodChainCustom = ({
  client,
  explorer,
}: { client: Client; explorer: Explorer }): Chain => ({
  id: 4663,
  network: 'robinhood',
  name: 'Robinhood Chain',
  nativeToken: {
    name: 'Ethereum',
    symbol: 'ETH',
    decimals: 18,
    currency: ethereum,
  },
  wrappedTokenAddresses: new Set<LowerHex>([
    '0x0bd7d308f8e1639fab988df18a8011f41eacad73',
  ]),
  explorer,
  client,
  blockTime: undefined,
  coingeckoId: undefined,
  defillamaId: undefined,
})

/** 公式 mainnet RPC。ログ取得は確認済みの 1000 block 単位に制限する。 */
export const robinhoodHttpRpcs: HttpRpc[] = [
  {
    url: 'https://rpc.mainnet.chain.robinhood.com',
    getLogsIsUsable: true,
    getLogsMaxBlockRange: 1_000n,
  },
]

/** Robinhood Chain の公開 WebSocket RPC 一覧。 */
export const robinhoodWebsocketRpcUrls: readonly string[] = []
